import React, { useState, useEffect, useRef, useCallback } from "react";
import { CKEditor } from "ckeditor4-react";
import TreeViewSidebar from "../components/TreeViewSidebar";
import LabeledInput from "../ui/LabeledInput";
import LabeledDropdown from "../ui/Dropdown";
import { useLocation } from "react-router-dom";
import apiRoutes from "../api/apiRoutes";
import { apiGet, apiPost, apiPostFormData } from "../api/request";
import { Button, Switch } from "antd";
import { toast } from "react-toastify";
import { BookAIcon, DraftingCompassIcon } from "lucide-react";
import { ReactComponent as DraftIcon } from "../assets/icons/draft.svg";
import { ReactComponent as VerifiedIcon } from "../assets/icons/verified.svg";
import GlowButton from "../components/GlowButton";
import { EyeOutlined } from "@ant-design/icons";

const Managebooks = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const bookId = query.get("id");
  const bookName = query.get("book") || "E-Book Editor";

  const [editorMode, setEditorMode] = useState("verified"); // "verified" | "draft"

  const [genreOptions, setGenreOptions] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    genre: "",
    parent: "",
    titleHeading: "",
    content: "",
  });
  const [editorContent, setEditorContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isFormDataInitialized, setIsFormDataInitialized] = useState(false);
  const editorInstanceRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const lastSelectedIdRef = useRef(null);
  const updateItemTimeoutRef = useRef(null);
  const updateEditorTimeoutRef = useRef(null);
  const isProgrammaticChangeRef = useRef(false);

  const getEditorContentByMode = (item, mode) => {
    if (!item) return "<p>Start writing here...</p>";

    if (mode === "verified") {
      return item.verified_content || "<p>No verified content yet.</p>";
    }

    return item.draft_content || "<p>Start writing here...</p>";
  };

  useEffect(() => {
    if (!selectedId || !editorInstanceRef.current) return;

    const selectedItem = findItemById(selectedId);
    if (!selectedItem) return;

    const content = getEditorContentByMode(selectedItem, editorMode);

    isProgrammaticChangeRef.current = true;
    editorInstanceRef.current.setData(content, {
      callback: () => {
        isProgrammaticChangeRef.current = false;
      },
    });

    setEditorContent(content);
    setFormData((prev) => ({ ...prev, content }));
  }, [editorMode, selectedId]);

  const ensureCoverId = (items, coverId) => {
    return items.map((item) => ({
      ...item,
      cover_id: parseInt(coverId, 10),
      children: item.children ? ensureCoverId(item.children, coverId) : [],
    }));
  };

  const sanitizeFetchedItems = (items) => {
    return items.map((item) => ({
      ...item,
      has_edit: false,
      cover_id: parseInt(bookId, 10),
      children: item.children ? sanitizeFetchedItems(item.children) : [],
    }));
  };

  useEffect(() => {
    let isMounted = true;

    const fetchBookContents = async () => {
      try {
        setIsLoading(true);
        const [bookContentRes, genreRes] = await Promise.all([
          apiGet(apiRoutes.getBookContents(bookId)),
          apiGet(apiRoutes.getGenres),
        ]);

        if (!isMounted) return;

        if (Array.isArray(genreRes.responsedata)) {
          setGenreOptions(
            genreRes.responsedata.map((genre) => ({
              label: genre.name,
              value: genre.id,
            })),
          );
        }

        if (Array.isArray(bookContentRes)) {
          console.log(
            "Raw fetched bookContentRes:",
            JSON.stringify(bookContentRes, null, 2),
          );
          const validItems = sanitizeFetchedItems(
            bookContentRes.filter((item) => item.id != null && item.id !== undefined),
          );
          console.log(
            "Processed menuItems:",
            JSON.stringify(validItems, null, 2),
          );
          setMenuItems(validItems);
        }
      } catch (err) {
        console.error("Error fetching data:", err.message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (bookId) {
      console.log("bookId:", bookId, "Parsed:", parseInt(bookId, 10));
      fetchBookContents();
    }

    return () => {
      isMounted = false;
    };
  }, [bookId]);

  const findItemById = (id, items = menuItems) => {
    if (id == null || id === undefined) {
      console.warn("findItemById called with invalid id:", id);
      return null;
    }

    if (!Array.isArray(items)) {
      console.warn("findItemById called with invalid items:", items);
      return null;
    }

    for (let item of items) {
      if (item.id == null || item.id === undefined) {
        console.warn("Item with invalid id found:", item);
        continue;
      }

      if (String(item.id) === String(id)) {
        return item;
      }

      if (item.children?.length) {
        const found = findItemById(id, item.children);
        if (found) return found;
      }
    }
    return null;
  };

  const setHasEditForNodeAndParents = (items, nodeId) => {
    return items.map((item) => {
      if (String(item.id) === String(nodeId)) {
        return { ...item, has_edit: true };
      }
      if (item.children?.length) {
        return {
          ...item,
          children: setHasEditForNodeAndParents(item.children, nodeId),
        };
      }
      return item;
    });
  };

  const addChildToParent = (items, parentId, newChild) => {
    return items.map((item) => {
      if (String(item.id) === String(parentId)) {
        return {
          ...item,
          children: [...(item.children || []), { ...newChild, has_edit: true }],
          has_edit: true,
        };
      }
      if (item.children?.length) {
        return {
          ...item,
          children: addChildToParent(item.children, parentId, newChild),
        };
      }
      return item;
    });
  };

  const findNodeAndSiblingIndex = (id, items, parentId = null) => {
    if (!items || !Array.isArray(items)) return null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (String(item.id) === String(id)) {
        return { item, index: i, parentId: parentId };
      }
      if (item.children?.length) {
        const found = findNodeAndSiblingIndex(id, item.children, item.id);
        if (found) return found;
      }
    }
    return null;
  };

  const updateMenuItemsFromTreeNodes = (treeNodes, draggedNodeId) => {
    const convertTreeNodesToMenuItems = (nodes, parentId = null) => {
      return nodes.map((node, index) => {
        const itemData = { ...node.data };
        itemData.parent_id = parentId;
        itemData.order = index + 1;
        itemData.cover_id = parseInt(bookId, 10);
        itemData.children = node.children
          ? convertTreeNodesToMenuItems(node.children, itemData.id)
          : [];

        const isDragged = draggedNodeId && String(itemData.id) === String(draggedNodeId);
        const prevInfo = findNodeAndSiblingIndex(itemData.id, menuItems);

        let hasEdit = itemData.has_edit || false;

        if (isDragged) {
          hasEdit = true;
        } else if (prevInfo) {
          const parentChanged = String(prevInfo.parentId ?? "") !== String(parentId ?? "");
          const indexChanged = prevInfo.index !== index;
          if (parentChanged || indexChanged) {
            hasEdit = true;
          }
        } else {
          hasEdit = true;
        }

        itemData.has_edit = hasEdit;
        return itemData;
      });
    };

    let updatedMenuItems = convertTreeNodesToMenuItems(treeNodes);

    if (draggedNodeId) {
      updatedMenuItems = setHasEditForNodeAndParents(
        updatedMenuItems,
        draggedNodeId,
      );
    }

    updatedMenuItems = ensureCoverId(updatedMenuItems, bookId);
    console.log(
      "Updated menuItems after drag-and-drop:",
      JSON.stringify(updatedMenuItems, null, 2),
    );
    return updatedMenuItems;
  };

  const handleDragDropUpdate = (updatedTreeNodes, draggedNodeId) => {
    console.log(
      "handleDragDropUpdate called with draggedNodeId:",
      draggedNodeId,
      "treeNodes:",
      updatedTreeNodes
    );
    const updatedMenuItems = updateMenuItemsFromTreeNodes(
      updatedTreeNodes,
      draggedNodeId,
    );
    setMenuItems(updatedMenuItems);
    console.log(
      "Set menuItems after drag-and-drop:",
      JSON.stringify(updatedMenuItems, null, 2),
    );
  };

  const deleteNode = (nodeId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this section and all its children?",
      )
    )
      return;

    const collectNodeIdsToDelete = (id, items) => {
      let ids = [id];
      const node = findItemById(id, items);
      if (node && node.children?.length) {
        node.children.forEach((child) => {
          ids = ids.concat(collectNodeIdsToDelete(child.id, items));
        });
      }
      return ids;
    };

    const idsToDelete = collectNodeIdsToDelete(nodeId, menuItems);
    setMenuItems((prevItems) => {
      const removeNodes = (items) => {
        return items
          .filter((item) => !idsToDelete.includes(item.id))
          .map((item) => ({
            ...item,
            children: item.children ? removeNodes(item.children) : [],
          }));
      };

      let updatedItems = removeNodes(prevItems);

      const deletedNode = findItemById(nodeId, menuItems);
      if (deletedNode && deletedNode.parent_id) {
        updatedItems = updatedItems.map((item) => {
          if (String(item.id) === String(deletedNode.parent_id)) {
            return {
              ...item,
              children: item.children.map((child, index) => ({
                ...child,
                order: index + 1,
                has_edit: true,
                cover_id: parseInt(bookId, 10),
              })),
              has_edit: true,
            };
          }
          return item;
        });
      }

      if (selectedId && idsToDelete.includes(selectedId)) {
        setSelectedId(null);
        setFormData({
          title: "",
          genre: "",
          parent: "",
          titleHeading: "",
          content: "",
        });
        setEditorContent("<p>Start writing here...</p>");
      }

      updatedItems = ensureCoverId(updatedItems, bookId);
      console.log(
        "Updated menuItems after deletion:",
        JSON.stringify(updatedItems, null, 2),
      );
      return updatedItems;
    });

    handleSave({ deletedIds: idsToDelete, mode: "draft" });
  };

  useEffect(() => {
    if (menuItems.length > 0 && !selectedId) {
      const firstValidItem = menuItems.find(
        (item) => item.id != null && item.id !== undefined,
      );
      if (firstValidItem) {
        setSelectedId(firstValidItem.id);
      } else {
        console.warn("No valid items with IDs found in menuItems");
      }
    }
  }, [menuItems]);

  useEffect(() => {
    if (!selectedId) return;

    const selectedItem = findItemById(selectedId);
    const content = getEditorContentByMode(selectedItem, editorMode);
    if (selectedItem) {
      const newFormData = {
        title: selectedItem.title || "",
        genre: selectedItem.genre?.id || "",
        parent: selectedItem.parent_id || "",
        titleHeading: selectedItem.header || "",
        content,
      };
      setFormData(newFormData);
      setEditorContent(content);

      setIsFormDataInitialized(true);

      if (editorInstanceRef.current) {
        const editor = editorInstanceRef.current;
        const newContent = getEditorContentByMode(selectedItem, editorMode);

        const currentContent = editor.getData();

        if (currentContent !== newContent) {
          isProgrammaticChangeRef.current = true;
          editor.setData(newContent, {
            callback: () => {
              isProgrammaticChangeRef.current = false;
            },
          });
        }
      }
    } else {
      console.warn("No item found for selectedId:", selectedId);
    }
  }, [selectedId]);

  const updateItem = (id, data) => {
    if (!id) {
      console.warn("updateItem called with invalid id:", id);
      return;
    }

    setMenuItems((prevItems) => {
      const updateNode = (items) => {
        return items.map((item) => {
          if (String(item.id) === String(id)) {
            return {
              ...item,
              ...data,
              has_edit: true,
              cover_id: parseInt(bookId, 10),
            };
          }
          if (item.children?.length) {
            return { ...item, children: updateNode(item.children) };
          }
          return item;
        });
      };

      let updatedItems = updateNode(prevItems);
      updatedItems = setHasEditForNodeAndParents(updatedItems, id);
      updatedItems = ensureCoverId(updatedItems, bookId);
      console.log("Updated menuItems:", JSON.stringify(updatedItems, null, 2));
      return updatedItems;
    });
  };

  const moveNode = (nodeId, newParentId) => {
    setMenuItems((prevItems) => {
      let nodeToMove = null;
      let oldParentId = null;

      const removeNode = (items) => {
        return items
          .map((item) => {
            if (String(item.id) === String(nodeId)) {
              nodeToMove = { ...item };
              oldParentId = item.parent_id;
              return null;
            }
            if (item.children?.length) {
              return { ...item, children: removeNode(item.children) };
            }
            return item;
          })
          .filter((item) => item !== null);
      };

      let updatedItems = removeNode(prevItems);

      if (!nodeToMove) {
        console.warn(`Node with id ${nodeId} not found for moving`);
        return prevItems;
      }

      nodeToMove.parent_id = newParentId || null;
      nodeToMove.cover_id = parseInt(bookId, 10);
      nodeToMove.has_edit = true;

      if (newParentId) {
        updatedItems = addChildToParent(updatedItems, newParentId, nodeToMove);
        updatedItems = updatedItems.map((item) => {
          if (String(item.id) === String(newParentId)) {
            return {
              ...item,
              children: item.children.map((child, index) => ({
                ...child,
                order: index + 1,
                has_edit: true,
                cover_id: parseInt(bookId, 10),
              })),
              has_edit: true,
            };
          }
          return item;
        });
      } else {
        nodeToMove.order = updatedItems.length + 1;
        updatedItems = [...updatedItems, nodeToMove];
      }

      if (oldParentId) {
        updatedItems = updatedItems.map((item) => {
          if (String(item.id) === String(oldParentId)) {
            return {
              ...item,
              children: item.children.map((child, index) => ({
                ...child,
                order: index + 1,
                has_edit: true,
                cover_id: parseInt(bookId, 10),
              })),
              has_edit: true,
            };
          }
          return item;
        });
      }

      updatedItems = ensureCoverId(updatedItems, bookId);
      console.log(
        "Updated menuItems after moving node:",
        JSON.stringify(updatedItems, null, 2),
      );
      return updatedItems;
    });
  };

  const debounceUpdateItem = (id, data) => {
    if (updateItemTimeoutRef.current) {
      clearTimeout(updateItemTimeoutRef.current);
    }
    updateItemTimeoutRef.current = setTimeout(() => {
      updateItem(id, data);
      updateItemTimeoutRef.current = null;
    }, 100);
  };

  const debounceEditorChange = useCallback(
    (content, currentSelectedId) => {
      if (updateEditorTimeoutRef.current) {
        clearTimeout(updateEditorTimeoutRef.current);
      }
      updateEditorTimeoutRef.current = setTimeout(() => {
        if (currentSelectedId !== selectedId) {
          return;
        }
        setFormData((prev) => ({ ...prev, content }));
        if (currentSelectedId) {
          const updatedData =
            editorMode === "verified"
              ? { verified_content: content }
              : { draft_content: content };

          debounceUpdateItem(currentSelectedId, updatedData);
        }
        updateEditorTimeoutRef.current = null;
      }, 100);
    },
    [selectedId],
  );

  const handleEditorChange = useCallback(
    (content) => {
      setEditorContent(content);
      debounceEditorChange(content, selectedId);
    },
    [selectedId, debounceEditorChange],
  );

  useEffect(() => {
    const editor = editorInstanceRef.current;
    if (!editor) return;

    const onChangeHandler = () => {
      if (isProgrammaticChangeRef.current) {
        return;
      }
      const newContent = editor.getData();
      handleEditorChange(newContent);
    };

    editor.on("change", onChangeHandler);

    return () => {
      editor.removeListener("change", onChangeHandler);
    };
  }, [handleEditorChange]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value };

      if (selectedId) {
        const selectedItem = findItemById(selectedId);
        if (!selectedItem) {
          return newFormData;
        }

        const updatedData = {};
        if (field === "title") updatedData.title = newFormData.title;
        if (field === "titleHeading")
          updatedData.header = newFormData.titleHeading;
        if (field === "content")
          updatedData.draft_content = newFormData.content;
        if (field === "genre") {
          updatedData.genre_id = newFormData.genre;
          updatedData.genre = newFormData.genre
            ? {
              id: newFormData.genre,
              name:
                genreOptions.find(
                  (option) => option.value === newFormData.genre,
                )?.label || "",
            }
            : selectedItem.genre;
        }
        if (field === "parent") {
          updatedData.parent_id = newFormData.parent || null;
          moveNode(selectedId, newFormData.parent);
        }

        if (Object.keys(updatedData).length > 0) {
          debounceUpdateItem(selectedId, updatedData);
        }
      }

      return newFormData;
    });
  };

  const allItemsAsOptions = (items, options = []) => {
    if (!Array.isArray(items)) {
      return options;
    }

    for (let item of items) {
      if (item.id == null || item.id === undefined) {
        continue;
      }

      if (String(item.id) !== String(selectedId)) {
        options.push({
          value: item.id,
          label: item.title || item.genre?.name || "Untitled",
        });
      }
      if (item.children?.length) {
        allItemsAsOptions(item.children, options);
      }
    }
    return options;
  };

  function flattenContentTree(tree, parentCoverId = bookId, depth = 0) {
    let flatList = [];
    const parsedParentCoverId = parseInt(parentCoverId, 10) || null;
    const createdBy = 1; // Replace with actual user ID
    const createdOn = new Date().toISOString();

    tree.forEach((node) => {
      const nodeId = node.id;
      const parentId = node.parent_id || null;

      if (node.has_edit) {
        flatList.push({
          id: nodeId,
          title: node.title || "Untitled",
          header: node.header || "",
          draft_content: node.draft_content || "",
          verified_content: node.verified_content || "",
          is_verified: node.is_verified || false,
          order: node.order || 1,
          genre_id: node.genre_id || null,
          cover_id: parsedParentCoverId,
          parent_id: parentId,
          created_by: node.created_by || createdBy,
          created_on: node.created_on || createdOn,
          depth: depth,
        });
      }

      if (node.children && node.children.length > 0) {
        flatList = flatList.concat(
          flattenContentTree(node.children, parsedParentCoverId, depth + 1),
        );
      }
    });

    return flatList;
  }

  const flattenMenuItemsForLog = (items) => {
    let flatList = [];
    items.forEach((item) => {
      flatList.push({
        title: item.title,
        id: item.id,
        parent_id: item.parent_id,
        cover_id: item.cover_id,
        order: item.order,
      });
      if (item.children && item.children.length > 0) {
        flatList = flatList.concat(flattenMenuItemsForLog(item.children));
      }
    });
    return flatList;
  };

  const base64ToBlob = (base64Data, contentType = "") => {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  };

  const uploadBase64ImagesInHtml = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return htmlContent || "";

    const dataUriRegex = /data:image\/([a-zA-Z0-9+.=-]+);base64,([A-Za-z0-9+/=]+)/g;
    let match;
    let updatedHtml = htmlContent;
    const matches = [];

    while ((match = dataUriRegex.exec(htmlContent)) !== null) {
      matches.push({
        fullDataUri: match[0],
        mimeType: match[1],
        base64Str: match[2],
      });
    }

    if (matches.length === 0) return htmlContent;

    for (const item of matches) {
      try {
        const blob = base64ToBlob(item.base64Str, `image/${item.mimeType}`);
        const ext = item.mimeType === "jpeg" ? "jpg" : item.mimeType;
        const file = new File([blob], `embedded-image-${Date.now()}.${ext}`, {
          type: `image/${item.mimeType}`,
        });

        const formData = new FormData();
        formData.append("file", file);

        const response = await apiPostFormData(apiRoutes.uploadAttachment, formData);
        const uploadedUrl =
          response?.data?.url ||
          response?.data?.fileUrl ||
          response?.data?.path ||
          response?.data?.responsedata ||
          response?.data?.data?.url ||
          response?.data?.data?.path;

        if (uploadedUrl) {
          updatedHtml = updatedHtml.replaceAll(item.fullDataUri, uploadedUrl);
        }
      } catch (err) {
        console.error("Failed to upload base64 image:", err);
      }
    }

    return updatedHtml;
  };

  const handleSave = async ({ deletedIds = [] } = {}) => {
    let currentMenuItems = menuItems;

    if (selectedId && editorInstanceRef.current) {
      const currentEditorContent = editorInstanceRef.current.getData();
      const fieldToUpdate =
        editorMode === "verified"
          ? { verified_content: currentEditorContent, is_verified: true, has_edit: true }
          : { draft_content: currentEditorContent, is_verified: false, has_edit: true };

      const updateNodeContent = (nodes) => {
        return nodes.map((node) => {
          if (String(node.id) === String(selectedId)) {
            return { ...node, ...fieldToUpdate };
          }
          if (node.children?.length) {
            return { ...node, children: updateNodeContent(node.children) };
          }
          return node;
        });
      };

      currentMenuItems = updateNodeContent(currentMenuItems);
    }

    const rawFlattened = flattenContentTree(currentMenuItems);

    // Sort by depth first, then by order, guaranteeing parents are processed before children
    rawFlattened.sort((a, b) => {
      if (a.depth !== b.depth) {
        return a.depth - b.depth;
      }
      return a.order - b.order;
    });

    try {
      setIsLoading(true);

      const flattened = await Promise.all(
        rawFlattened.map(async ({ depth, ...item }) => {
          const cleanDraft = await uploadBase64ImagesInHtml(item.draft_content || "");
          const cleanVerified = await uploadBase64ImagesInHtml(item.verified_content || "");

          if (editorMode === "verified") {
            return {
              ...item,
              is_verified: true,
              verified_content: cleanVerified,
              draft_content: cleanVerified,
            };
          }

          return {
            ...item,
            is_verified: false,
            draft_content: cleanDraft,
            verified_content: cleanVerified,
          };
        })
      );

      console.log("Flattened items count:", flattened.length);
      const payload = { contents: flattened, deletedIds };
      console.log("Payload total size (bytes):", JSON.stringify(payload).length);

      const response = await apiPost(apiRoutes.saveBookContetnt, payload);
      console.log("API Response:", JSON.stringify(response, null, 2));

      if (response?.status === 200 || response?.data?.status === 200) {
        toast.success(
          response?.responsedata ||
          response?.data?.responsedata ||
          "Content saved successfully",
        );
        const bookContentRes = await apiGet(apiRoutes.getBookContents(bookId));
        if (Array.isArray(bookContentRes)) {
          const validItems = sanitizeFetchedItems(
            bookContentRes.filter((item) => item.id != null && item.id !== undefined),
          );
          console.log(
            "Refreshed menuItems:",
            JSON.stringify(validItems, null, 2),
          );
          setMenuItems(validItems);
        }
      }
    } catch (err) {
      console.error("Error saving data:", err.message, err.response);
      toast.error(`Failed to save content: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    try {
      const editor = editorInstanceRef.current;
      if (!editor) {
        toast.error("Editor not ready");
        return;
      }

      const htmlContent = editor.getData() || "<p>No content</p>";

      // store temporarily (safe + simple)
      sessionStorage.setItem("ebookPreviewHtml", htmlContent);
      sessionStorage.setItem("ebookPreviewBook", bookName);

      // open proper preview route
      window.open("/editor-preview", "_blank");
    } catch (err) {
      console.error("Preview error:", err);
      toast.error("Failed to open preview");
    }
  };

  const handleAddNode = (newNodeData) => {
    if (
      !newNodeData ||
      newNodeData.id == null ||
      newNodeData.id === undefined
    ) {
      console.warn("New node has invalid ID:", newNodeData);
      return;
    }

    console.log("Received newNodeData:", JSON.stringify(newNodeData, null, 2));
    const newNodeWithEdit = {
      ...newNodeData,
      cover_id: parseInt(bookId, 10),
      has_edit: true,
    };

    setMenuItems((prev) => {
      let updatedItems;

      if (newNodeData.parent_id != null) {
        updatedItems = addChildToParent(
          prev,
          newNodeData.parent_id,
          newNodeWithEdit,
        );
      } else {
        updatedItems = [...prev, newNodeWithEdit];
      }

      if (newNodeData.parent_id != null) {
        updatedItems = setHasEditForNodeAndParents(
          updatedItems,
          newNodeData.parent_id,
        );
      }

      updatedItems = ensureCoverId(updatedItems, bookId);
      console.log(
        "Updated menuItems with new node:",
        JSON.stringify(updatedItems, null, 2),
      );
      return updatedItems;
    });
  };

  const debounceSetSelectedId = (id) => {
    if (lastSelectedIdRef.current === id) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setSelectedId(id);
    lastSelectedIdRef.current = id;

    debounceTimeoutRef.current = setTimeout(() => {
      debounceTimeoutRef.current = null;
    }, 100);
  };

  return (
    <div
      style={{
        padding: "0.25rem",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          width: "99vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "4px 18px",
          background: "#000000",
          marginBottom: "0.5rem",
          borderRadius: "0.3rem",
        }}
      >
        {/* LEFT — Book name */}
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#ffffff",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "40%",
          }}
        >
          {bookName}
        </div>

        {/* RIGHT — View switch */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            background: "#000",
            padding: "2px 12px",
            borderRadius: "12px",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              color: "#f8c90c",
              opacity: 0.8,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            View as:
          </span>

          <span
            style={{
              fontSize: "12px",
              color: "#ffffff",
              opacity: editorMode === "verified" ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <DraftIcon width={14} height={14} />
            DRAFT
          </span>

          <Switch
            checked={editorMode === "verified"}
            onChange={(checked) =>
              setEditorMode(checked ? "verified" : "draft")
            }
            style={{
              backgroundColor: editorMode === "verified" ? "#22c55e" : "#444",
            }}
          />

          <span
            style={{
              fontSize: "12px",
              color: "#fff",
              opacity: editorMode === "verified" ? 1 : 0.5,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <VerifiedIcon width={14} height={14} />
            VERIFIED
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
        <div
          style={{
            minWidth: "300px",
            maxHeight: "calc(100vh - 55px)",
            overflowY: "auto",
            backgroundColor: "#0e1a2b",
            borderRadius: "0.3rem",
          }}
        >
          <TreeViewSidebar
            ebookContentData={menuItems}
            selectedId={selectedId}
            onNodeSelect={(selectedItem) => {
              if (
                selectedItem &&
                selectedItem.id != null &&
                selectedItem.id !== undefined
              ) {
                debounceSetSelectedId(selectedItem.id);
              }
            }}
            onAddNode={handleAddNode}
            onDragDropUpdate={handleDragDropUpdate}
            onDeleteNode={deleteNode}
          />
        </div>

        <div style={{ flexGrow: 1, marginRight: "1rem" }}>
          {isLoading ? (
            <p>Loading...</p>
          ) : menuItems.length === 0 || !selectedId ? (
            <p>No items available. Add a new node to start editing.</p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: "1rem",
                  marginBottom: "1rem",
                  alignItems: "center",
                }}
              >
                <LabeledInput
                  id="title"
                  label="Title"
                  placeholder="Enter Title"
                  value={formData.title}
                  onChange={(e) => handleFieldChange("title", e.target.value)}
                />
                <LabeledDropdown
                  label="Genre"
                  options={genreOptions}
                  placeholder="Select Genre"
                  value={formData.genre}
                  onChange={(val) => handleFieldChange("genre", val)}
                />
                <LabeledDropdown
                  label="Parent"
                  options={allItemsAsOptions(menuItems)}
                  placeholder="Select Parent"
                  value={formData.parent}
                  onChange={(val) => handleFieldChange("parent", val)}
                />
                <LabeledInput
                  id="heading"
                  label="Heading"
                  placeholder="Heading"
                  value={formData.titleHeading}
                  onChange={(e) =>
                    handleFieldChange("titleHeading", e.target.value)
                  }
                />

                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    justifyContent: "flex-end",
                    marginTop: "1rem",
                  }}
                >
                  {/* Preview */}
                  <Button
                    type="default"
                    shape="default"
                    icon={<EyeOutlined style={{ color: "#000" }} />}
                    onClick={handlePreview}
                    style={{
                      background: "#f2f2ed",
                      borderColor: "#111827",
                      marginTop: "4px",
                      marginRight: "10px",
                      cursor: "pointer",
                    }}
                  />

                  {/* Save */}
                  <GlowButton type="primary" onClick={() => handleSave()}>
                    {editorMode === "verified"
                      ? "Save As Verified"
                      : "Save As Draft"}
                  </GlowButton>
                </div>
              </div>
              <div style={{ marginBottom: "1rem", fontSize: "0.875rem" }}>
                {isFormDataInitialized && (
                  <CKEditor
                    config={{
                      height: "calc(75vh - 65px)",
                      autoParagraph: false,
                      extraPlugins:
                        "notification,accordion, popovereditor,filelink",
                      toolbar: [
                        {
                          name: "document",
                          items: [
                            "Source",
                            "-",
                            "NewPage",
                            "Preview",
                            "-",
                            "Templates",
                          ],
                        },
                        {
                          name: "clipboard",
                          items: [
                            "Cut",
                            "Copy",
                            "Paste",
                            "PasteText",
                            "PasteFromWord",
                            "-",
                            "Undo",
                            "Redo",
                          ],
                        },
                        {
                          name: "editing",
                          items: [
                            "Find",
                            "Replace",
                            "-",
                            "SelectAll",
                            "-",
                            "Scayt",
                          ],
                        },
                        {
                          name: "basicstyles",
                          items: [
                            "Bold",
                            "Italic",
                            "Underline",
                            "Strike",
                            "Subscript",
                            "Superscript",
                            "-",
                            "RemoveFormat",
                          ],
                        },
                        {
                          name: "paragraph",
                          items: [
                            "NumberedList",
                            "BulletedList",
                            "-",
                            "Outdent",
                            "Indent",
                            "-",
                            "Blockquote",
                            "CreateDiv",
                            "-",
                            "JustifyLeft",
                            "JustifyCenter",
                            "JustifyRight",
                            "JustifyBlock",
                            "-",
                            "BidiLtr",
                            "BidiRtl",
                            "Language",
                          ],
                        },
                        { name: "links", items: ["Link", "Unlink", "Anchor"] },
                        {
                          name: "insert",
                          items: [
                            "Image",
                            "Flash",
                            "Table",
                            "HorizontalRule",
                            "Smiley",
                            "SpecialChar",
                            "PageBreak",
                            "Iframe",
                          ],
                        },
                        {
                          name: "styles",
                          items: ["Styles", "Format", "Font", "FontSize"],
                        },
                        { name: "colors", items: ["TextColor", "BGColor"] },
                        { name: "tools", items: ["Maximize", "ShowBlocks"] },
                        {
                          name: "custom",
                          items: [
                            "Notification",
                            "Accordion",
                            "PopoverEditor",
                            "FileLink",
                          ],
                        },
                      ],
                      allowedContent: true,
                      removePlugins: "elementspath",
                    }}
                    onBeforeLoad={(CKEDITOR) => {
                      console.log("Loading plugins...");
                      if (CKEDITOR.plugins.get("accordion") === undefined) {
                        CKEDITOR.plugins.addExternal(
                          "accordion",
                          "/ckeditor/plugins/accordion/",
                          "plugin.js",
                        );
                        console.log("Accordion plugin path added");
                      }
                      if (CKEDITOR.plugins.get("notification") === undefined) {
                        CKEDITOR.plugins.addExternal(
                          "notification",
                          "/ckeditor/plugins/notification/",
                          "plugin.js",
                        );
                        console.log("Notification plugin path added");
                      }
                      console.log(
                        "Plugins loaded, checking:",
                        CKEDITOR.plugins.get("notification"),
                      );
                    }}
                    onInstanceReady={({ editor }) => {
                      console.log(
                        "Editor instance ready, plugins:",
                        Object.keys(editor.plugins),
                      );
                      editorInstanceRef.current = editor;
                      const selectedItem = findItemById(selectedId);
                      const initialContent = getEditorContentByMode(
                        selectedItem,
                        editorMode,
                      );

                      isProgrammaticChangeRef.current = true;
                      editor.setData(initialContent, {
                        callback: () => {
                          isProgrammaticChangeRef.current = false;
                        },
                      });
                      setEditorContent(initialContent);
                    }}
                    onChange={(event) => {
                      if (!isProgrammaticChangeRef.current) {
                        handleEditorChange(event.editor.getData());
                      }
                    }}
                  />
                )}
              </div>
              {/* <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button type="primary" onClick={handleSave}>
                  Save
                </Button>
              </div> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Managebooks;
